"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Loader2, Mail } from "lucide-react";
import { InboxWithUsage } from "@/hooks/agents/email/use-agents";

interface SharedInboxDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inbox: InboxWithUsage | null;
  currentAgentId: string;
  onConfirm: () => void;
  isLoading?: boolean;
}

export function SharedInboxDialog({
  open,
  onOpenChange,
  inbox,
  currentAgentId,
  onConfirm,
  isLoading,
}: SharedInboxDialogProps) {
  if (!inbox) return null;

  // Filter out current agent from the list
  const otherAgents = inbox.used_by_agents.filter(
    (a) => a.id !== currentAgentId
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            <span>Inbox Already in Use</span>
          </DialogTitle>
          <DialogDescription>
            This inbox is being used by other email agents.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md mb-4">
            <Mail className="h-5 w-5 text-gray-500" />
            <div>
              <div className="font-medium">{inbox.address}</div>
              <Badge variant="outline" className="mt-1">
                {inbox.provider}
              </Badge>
            </div>
          </div>

          <div className="text-sm text-muted-foreground mb-2">
            Currently used by:
          </div>
          <ul className="space-y-1">
            {otherAgents.map((agent) => (
              <li
                key={agent.id}
                className="flex items-center gap-2 text-sm p-2 bg-yellow-50 rounded border border-yellow-200"
              >
                <span className="font-medium">{agent.name}</span>
              </li>
            ))}
          </ul>

          <p className="text-sm text-muted-foreground mt-4">
            Adding this inbox to your agent may cause duplicate processing of
            emails. Are you sure you want to continue?
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isLoading}
            className="bg-yellow-600 hover:bg-yellow-700"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Adding...
              </>
            ) : (
              "Add Anyway"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
